import { logClientError } from '@utils/server-log';
/**
 * Leitura das violações de validação que o servidor manda no trailer
 * `grpc-status-details-bin`.
 *
 * Sem isto, um pedido com três campos inválidos vira uma única mensagem
 * genérica: o `message` do erro gRPC não carrega qual campo quebrou qual
 * regra — quem carrega é o `google.rpc.BadRequest` dentro dos `details` do
 * status, com uma `FieldViolation` por regra.
 *
 * Toda a decodificação é defensiva: qualquer falha devolve lista vazia e o
 * chamador cai na mensagem genérica de sempre. Erro ao explicar um erro não
 * pode virar um segundo erro.
 */

import * as ErrorDetailsProto from '@proto/google/rpc/error_details';
import * as StatusProto from '@proto/google/rpc/status';

const STATUS_DETAILS_KEY = 'grpc-status-details-bin';
const BAD_REQUEST_TYPE = 'google.rpc.BadRequest';

export interface FieldViolation {
  /** Caminho do campo na request, como o servidor o nomeia. */
  field: string;
  description: string;
}

/** Extrai as violações de campo de um erro do grpc-web. */
export function getFieldViolations(error: unknown): FieldViolation[] {
  try {
    const encoded = readStatusDetails(error);
    if (!encoded) return [];

    const status = StatusProto.google.rpc.Status.deserialize(base64ToBytes(encoded));

    for (const detail of status.details ?? []) {
      // O type_url vem como "type.googleapis.com/google.rpc.BadRequest".
      if (!detail.typeUrl?.endsWith(BAD_REQUEST_TYPE)) continue;

      const badRequest = ErrorDetailsProto.google.rpc.BadRequest.deserialize(detail.value);
      return (badRequest.fieldViolations ?? []).map((violation) => ({
        field: violation.field,
        description: violation.description,
      }));
    }
  } catch (decodeError) {
    logClientError('[gRPC] Não foi possível ler as violações de campo:', decodeError);
  }

  return [];
}

/**
 * Junta as violações numa mensagem só, uma por linha.
 * Devolve string vazia quando não há nada estruturado a dizer.
 */
export function formatFieldViolations(error: unknown): string {
  const violations = getFieldViolations(error);
  if (!violations.length) return '';

  return violations
    .map((violation) =>
      violation.field ? `${violation.field}: ${violation.description}` : violation.description
    )
    .join('\n');
}

function readStatusDetails(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;

  const metadata = (error as { metadata?: Record<string, unknown> }).metadata;
  const value = metadata?.[STATUS_DETAILS_KEY];

  return typeof value === 'string' && value ? value : null;
}

function base64ToBytes(value: string): Uint8Array {
  // O trailer usa base64url sem padding; `atob` só entende o alfabeto padrão.
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '='
  );

  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
